'use client';

import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
    AlertTriangle,
    ArrowRight,
    CheckCircle2,
    Circle,
    Clock,
    Download,
    ExternalLink,
    FileSignature,
    Loader2,
    Plus,
    Send,
    Shield,
    XCircle,
    Zap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
    STANDARD_CRM_BUY_RATE_GRID,
    cloneCrmBuyRateGrid,
} from '@tagadapay/billing-core/defaults/crm-buy-rate';
import {
    DEFAULT_RX_TRANSACTION_LINE,
    RX_PUBLIC_SELL_BPS,
    resolveRxSellBps,
} from '@tagadapay/billing-core/compute/rx-fees';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trpc } from '@/lib/api/trpc';
import { usePartnerAdmin } from '@/hooks/use-partner-admin';
import { useSuperAdmin } from '@/hooks/use-superadmin';
import { Link } from 'react-router-dom';
import { ADMIN_ROUTES } from '@/config/router';
import {
    ClauseEditor,
    type ContractClauseTemplate,
} from '@/components/admin/crm-buy-rate-clause-editor';
import { CrmBuyRateVersionHistory } from '@/components/admin/crm-buy-rate-version-history';

// ─── Fee category display config ────────────────────────────────────

type CategoryKey =
    | 'weeklyBase'
    | 'approvedTransaction'
    | 'declinedTransaction'
    | 'refund'
    | 'threeDsVerification'
    | 'subclubTransaction'
    | 'chargeback'
    | 'latePayment'
    | 'automaticRetry'
    | 'upsell'
    | 'subscription'
    | 'applePay'
    | 'googlePay'
    | 'applePayDomain'
    | 'customDomain'
    | 'rxTransaction';

type CategoryType = 'fixedPlusBps' | 'bpsOnly' | 'flat' | 'weeklyBase' | 'rx';

const CATEGORIES: { key: CategoryKey; label: string; type: CategoryType }[] = [
    { key: 'weeklyBase', label: 'Weekly Base Fee', type: 'weeklyBase' },
    { key: 'approvedTransaction', label: 'Approved Transaction', type: 'fixedPlusBps' },
    { key: 'declinedTransaction', label: 'Declined Transaction', type: 'fixedPlusBps' },
    { key: 'refund', label: 'Refund', type: 'fixedPlusBps' },
    { key: 'threeDsVerification', label: '3DS Verification', type: 'fixedPlusBps' },
    { key: 'subclubTransaction', label: 'SubClub Transaction', type: 'fixedPlusBps' },
    { key: 'chargeback', label: 'Chargeback', type: 'fixedPlusBps' },
    { key: 'latePayment', label: 'Late Payment', type: 'fixedPlusBps' },
    { key: 'automaticRetry', label: 'Automatic Retries', type: 'bpsOnly' },
    { key: 'upsell', label: 'Upsells', type: 'bpsOnly' },
    { key: 'subscription', label: 'Subscriptions', type: 'bpsOnly' },
    { key: 'applePay', label: 'Apple Pay', type: 'bpsOnly' },
    { key: 'googlePay', label: 'Google Pay', type: 'bpsOnly' },
    { key: 'applePayDomain', label: 'Tagada Apple Pay Domain', type: 'flat' },
    { key: 'customDomain', label: 'Custom Domain', type: 'flat' },
    // Tagada Rx: present + enabled on the active signed version = the partner
    // can sell Rx. Sell floor 3.90% unless `allowBelowPublic` (then the buy).
    { key: 'rxTransaction', label: 'Transaction TagadaRx', type: 'rx' },
];

function formatCents(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
}

function formatBps(bps: number): string {
    return `${(bps / 100).toFixed(2)}%`;
}

function formatLine(cat: typeof CATEGORIES[number], value: any): string {
    if (!value) return '—';
    if (cat.type === 'weeklyBase') {
        return value.enabled
            ? `${formatCents(value.amountCents)}${value.minApprovedTxns != null ? ` (min ${value.minApprovedTxns} txns)` : ''}`
            : 'Disabled';
    }
    if (cat.type === 'fixedPlusBps') {
        return value.enabled
            ? `${formatCents(value.fixedCents)} + ${formatBps(value.bps)}`
            : 'Disabled';
    }
    if (cat.type === 'bpsOnly') {
        return value.enabled ? formatBps(value.bps) : 'Disabled';
    }
    if (cat.type === 'flat') {
        return value.enabled ? formatCents(value.fixedCents) : 'Disabled';
    }
    if (cat.type === 'rx') {
        if (!value.enabled) return 'Disabled';
        return `${formatBps(value.bps)}${value.allowBelowPublic ? ' · below public price allowed' : ''}`;
    }
    return '—';
}

function formatMarkup(cat: typeof CATEGORIES[number], value: any): string {
    if (!value) return '—';
    if (cat.type === 'weeklyBase') return formatCents(value.amountCents ?? 0);
    if (cat.type === 'fixedPlusBps') return `${formatCents(value.fixedCents ?? 0)} + ${formatBps(value.bps ?? 0)}`;
    if (cat.type === 'bpsOnly' || cat.type === 'rx') return formatBps(value.bps ?? 0);
    if (cat.type === 'flat') return formatCents(value.fixedCents ?? 0);
    return '—';
}

// ─── Standard CRM Buy Rate preset (Tagada default) ──────────────────

/**
 * Tagada's default wholesale CRM Buy Rate. Pre-loaded in the "Propose New
 * Rate" dialog so an operator doesn't have to re-enter the whole grid for
 * every partner — they can tweak from here or hit "Reset to standard".
 * Amounts are in cents; rates in basis points (100 bps = 1.00%).
 *
 * Source of truth for the numbers:
 * `@tagadapay/billing-core/defaults/crm-buy-rate` — shared with the contract
 * scripts so the operator preset and the signed PDFs can never drift apart.
 */
function makeStandardGrid(): Record<string, any> {
    // Deep clone so dialog edits never mutate the shared preset.
    return cloneCrmBuyRateGrid(STANDARD_CRM_BUY_RATE_GRID) as Record<string, any>;
}

// ─── Main page ──────────────────────────────────────────────────────

export default function PartnerBillingPage() {
    const { partner: partnerParam } = useParams<{ partner: string }>();
    const slug = partnerParam ?? '';
    const { canView, partner, isLoading: loadingPartner } = usePartnerAdmin(partnerParam);
    const { isSuperAdmin } = useSuperAdmin();
    const isPartnerSelf = canView && !isSuperAdmin;

    // Superadmin queries (admin endpoints)
    const adminActiveQuery = trpc.partnerBuyRate.getActive.useQuery(
        { partnerSlug: slug },
        { enabled: isSuperAdmin && slug.length > 0 },
    );
    const adminMarkupQuery = trpc.partnerBuyRate.getMarkup.useQuery(
        { partnerSlug: slug },
        { enabled: isSuperAdmin && slug.length > 0 },
    );
    const eventsQuery = trpc.partnerBuyRate.listEvents.useQuery(
        { partnerSlug: slug },
        { enabled: isSuperAdmin && slug.length > 0 },
    );

    // Partner self-service queries
    const selfActiveQuery = trpc.partnerBuyRate.selfGetActiveBuyRate.useQuery(
        { partner: slug },
        { enabled: isPartnerSelf && slug.length > 0 },
    );
    const selfMarkupQuery = trpc.partnerBuyRate.selfGetMarkup.useQuery(
        { partner: slug },
        { enabled: isPartnerSelf && slug.length > 0 },
    );
    // Latest signature row (any status) — powers the lifecycle panel.
    const latestSigQuery = trpc.partnerBuyRate.getLatestSignature.useQuery(
        { partnerSlug: slug },
        { enabled: isSuperAdmin && slug.length > 0 },
    );
    const selfLatestSigQuery = trpc.partnerBuyRate.selfGetLatestSignature.useQuery(
        { partner: slug },
        { enabled: isPartnerSelf && slug.length > 0 },
    );

    // Unified data accessors
    const activeQuery = isSuperAdmin ? adminActiveQuery : selfActiveQuery;
    const markupQuery = isSuperAdmin ? adminMarkupQuery : selfMarkupQuery;
    const latestSignatureQuery = isSuperAdmin ? latestSigQuery : selfLatestSigQuery;

    if (loadingPartner) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-64 w-full" />
            </div>
        );
    }

    if (!canView || !partner) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center">
                <div className="text-center">
                    <Shield className="mx-auto h-12 w-12 text-muted-foreground" />
                    <h2 className="mt-4 text-xl font-semibold">Access Denied</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                        You don't have access to billing for this partner.
                    </p>
                </div>
            </div>
        );
    }

    const activeRate = activeQuery.data;
    const currentMarkup = markupQuery.data;
    const latestSig = latestSignatureQuery.data;
    const events = eventsQuery.data ?? [];

    return (
        <div className="space-y-6">
            {/* Signature lifecycle — shows every step so we can see where it blocks */}
            {latestSig && (
                <SignatureLifecycle
                    sig={latestSig as any}
                    isSuperAdmin={isSuperAdmin}
                    isLoading={latestSignatureQuery.isLoading}
                />
            )}

            {isSuperAdmin && (
                <PartnerContractTemplateCard partnerSlug={slug} />
            )}

            {/* Active Buy Rate */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <FileSignature className="h-5 w-5" />
                                {isSuperAdmin ? 'Active CRM Buy Rate' : 'Your CRM Buy Rate'}
                            </CardTitle>
                            <CardDescription>
                                {isSuperAdmin
                                    ? 'The signed rate grid between Tagada and this partner.'
                                    : 'The rate grid you signed with Tagada. This is your base cost per action.'}
                                {activeRate && (
                                    <span className="ml-1">
                                        Version {activeRate.version} — signed{' '}
                                        {activeRate.effectiveAt
                                            ? new Date(activeRate.effectiveAt).toLocaleDateString()
                                            : 'N/A'}
                                    </span>
                                )}
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            {activeRate && (
                                <ViewSignedContractButton
                                    partnerSlug={slug}
                                    isSuperAdmin={isSuperAdmin}
                                />
                            )}
                            {isSuperAdmin && (
                                <Button asChild variant="outline" size="sm">
                                    <Link to={ADMIN_ROUTES.CRM_CONTRACT_TEMPLATE}>Contract templates</Link>
                                </Button>
                            )}
                            {isSuperAdmin && <ProposeDialog partnerSlug={slug} />}
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {activeQuery.isLoading ? (
                        <div className="space-y-2">
                            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-6 w-full" />)}
                        </div>
                    ) : !activeRate ? (
                        <p className="text-sm text-muted-foreground">
                            {isSuperAdmin
                                ? 'No active buy rate. Use the "Propose New Rate" button to create and send one for signature.'
                                : 'No buy rate has been signed yet. Contact your Tagada account manager to set up your pricing.'}
                        </p>
                    ) : (
                        <BuyRateGrid
                            grid={activeRate.grid as any}
                            markup={currentMarkup as any}
                            hideMarkupColumn={isPartnerSelf}
                        />
                    )}
                </CardContent>
            </Card>

            {/* Markup Editor — visible to both superadmin and partner admin */}
            {activeRate && (
                <MarkupEditor
                    partnerSlug={slug}
                    currentMarkup={currentMarkup as any}
                    buyRateGrid={activeRate.grid as any}
                    isSuperAdmin={isSuperAdmin}
                />
            )}

            {/* Version History + per-version diffs — superadmin & partner */}
            {(isSuperAdmin || isPartnerSelf) && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Version History</CardTitle>
                        <CardDescription>
                            {isSuperAdmin
                                ? 'Every proposed buy rate version, with the changes introduced by each one.'
                                : 'Each contract version sent to you and what changed between them.'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <CrmBuyRateVersionHistory partnerSlug={slug} isSuperAdmin={isSuperAdmin} />
                    </CardContent>
                </Card>
            )}

            {/* Audit Log — superadmin only */}
            {isSuperAdmin && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Audit Log</CardTitle>
                        <CardDescription>Recent buy rate events for this partner.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {eventsQuery.isLoading ? (
                            <Skeleton className="h-24 w-full" />
                        ) : events.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No events yet.</p>
                        ) : (
                            <div className="space-y-2">
                                {events.map((e: any) => (
                                    <div key={e.id} className="flex items-start gap-3 rounded-md border p-3 text-sm">
                                        <div className="flex-1">
                                            <span className="font-medium">{e.eventType}</span>
                                            {e.actorEmail && (
                                                <span className="ml-2 text-muted-foreground">by {e.actorEmail}</span>
                                            )}
                                            {e.payload?.note && (
                                                <p className="mt-1 text-xs text-muted-foreground">{e.payload.note}</p>
                                            )}
                                        </div>
                                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                                            {e.createdAt ? new Date(e.createdAt).toLocaleString() : ''}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

// ─── Buy Rate Grid display (3 columns: buy rate, markup, sell rate) ─

function BuyRateGrid({ grid, markup, hideMarkupColumn }: { grid: Record<string, any>; markup?: Record<string, any> | null; hideMarkupColumn?: boolean }) {
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead className="w-[200px]">Category</TableHead>
                    <TableHead>{hideMarkupColumn ? 'Your Cost (Tagada)' : 'Buy Rate (Tagada)'}</TableHead>
                    {!hideMarkupColumn && <TableHead>Markup (Partner)</TableHead>}
                    {!hideMarkupColumn && <TableHead>Sell Rate (Merchant)</TableHead>}
                </TableRow>
            </TableHeader>
            <TableBody>
                {CATEGORIES.map((cat) => {
                    const buyLine = grid?.[cat.key];
                    if (!buyLine) return null;

                    const mkLine = markup?.[cat.key];
                    const sellLine = computeSellLine(cat, buyLine, mkLine);

                    return (
                        <TableRow key={cat.key}>
                            <TableCell className="font-medium">{cat.label}</TableCell>
                            <TableCell className="font-mono text-sm">{formatLine(cat, buyLine)}</TableCell>
                            {!hideMarkupColumn && (
                                <TableCell className="font-mono text-sm text-blue-600">
                                    {mkLine ? formatMarkup(cat, mkLine) : '—'}
                                </TableCell>
                            )}
                            {!hideMarkupColumn && (
                                <TableCell className="font-mono text-sm font-semibold">
                                    {sellLine}
                                </TableCell>
                            )}
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}

function computeSellLine(cat: typeof CATEGORIES[number], buy: any, mk: any): string {
    if (!buy?.enabled && buy?.enabled !== undefined) return 'Disabled';
    // Rx: sell = max(buy + markup, floor), floor = public price unless the
    // signed version allows selling below it.
    if (cat.type === 'rx') return formatBps(resolveRxSellBps(buy, mk?.bps ?? 0));
    if (!mk) return formatLine(cat, buy);

    if (cat.type === 'weeklyBase') {
        const total = (buy.amountCents ?? 0) + (mk.amountCents ?? 0);
        return formatCents(total);
    }
    if (cat.type === 'fixedPlusBps') {
        const totalFixed = (buy.fixedCents ?? 0) + (mk.fixedCents ?? 0);
        const totalBps = (buy.bps ?? 0) + (mk.bps ?? 0);
        return `${formatCents(totalFixed)} + ${formatBps(totalBps)}`;
    }
    if (cat.type === 'bpsOnly') {
        return formatBps((buy.bps ?? 0) + (mk.bps ?? 0));
    }
    if (cat.type === 'flat') {
        return formatCents((buy.fixedCents ?? 0) + (mk.fixedCents ?? 0));
    }
    return '—';
}

// ─── Status badge ───────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
        draft: { variant: 'outline', label: 'Draft' },
        pending_signature: { variant: 'secondary', label: 'Pending Signature' },
        active: { variant: 'default', label: 'Active' },
        superseded: { variant: 'outline', label: 'Superseded' },
        rejected: { variant: 'destructive', label: 'Rejected' },
        expired: { variant: 'destructive', label: 'Expired' },
        cancelled: { variant: 'outline', label: 'Cancelled' },
    };
    const config = variants[status] ?? { variant: 'outline' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
}

// ─── Signature lifecycle (per-step progress + audit) ────────────────

type StepState = 'done' | 'current' | 'pending' | 'blocked';

interface SignatureRow {
    status: string;
    firmaStatus?: string | null;
    firmaContractId?: string | null;
    firmaSigningUrl?: string | null;
    proposedAt?: string | Date | null;
    createdAt?: string | Date | null;
    partnerSignedAt?: string | Date | null;
    tagadaSignedAt?: string | Date | null;
    signedAt?: string | Date | null;
    effectiveAt?: string | Date | null;
    partnerSignerName?: string | null;
    partnerSignerTitle?: string | null;
    partnerSignerEmail?: string | null;
    signedByName?: string | null;
    signedByEmail?: string | null;
    tagadaSignerName?: string | null;
    tagadaSignerEmail?: string | null;
    proposedByEmail?: string | null;
    recipientsState?: Array<{
        party?: string;
        email?: string | null;
        name?: string | null;
        signedAt?: string | null;
        firmaEventId?: string | null;
        firmaEventType?: string | null;
    }> | null;
}

function fmtDateTime(v?: string | Date | null): string {
    if (!v) return '';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

const SIG_STATUS_META: Record<string, { label: string; tone: string; description: string }> = {
    pending_signature: { label: 'Awaiting partner signature', tone: 'amber', description: 'The envelope was sent. We are waiting for the partner to sign.' },
    partner_signed: { label: 'Awaiting TagadaPay counter-signature', tone: 'blue', description: 'The partner signed. We are waiting for TagadaPay to counter-sign.' },
    signed: { label: 'Fully signed & active', tone: 'green', description: 'All parties signed. The buy rate is active.' },
    cancelled: { label: 'Cancelled', tone: 'gray', description: 'This signing request was cancelled (usually superseded by a new proposal).' },
    superseded: { label: 'Superseded', tone: 'gray', description: 'Replaced by a later signed version.' },
    expired: { label: 'Expired', tone: 'red', description: 'The Firma envelope expired before all parties signed.' },
    rejected: { label: 'Declined', tone: 'red', description: 'A party explicitly declined to sign.' },
};

function StepIcon({ state }: { state: StepState }) {
    if (state === 'done') return <CheckCircle2 className="h-5 w-5 text-green-600" />;
    if (state === 'current') return <Clock className="h-5 w-5 text-amber-600" />;
    if (state === 'blocked') return <XCircle className="h-5 w-5 text-red-600" />;
    return <Circle className="h-5 w-5 text-muted-foreground/40" />;
}

function SignatureLifecycle({
    sig,
    isSuperAdmin,
    isLoading,
}: {
    sig: SignatureRow;
    isSuperAdmin: boolean;
    isLoading?: boolean;
}) {
    const status = sig.status;
    const meta = SIG_STATUS_META[status] ?? { label: status, tone: 'gray', description: '' };
    const isTerminalFail = status === 'expired' || status === 'rejected' || status === 'cancelled';
    const hasTagadaSigner = Boolean(sig.tagadaSignerName || sig.tagadaSignerEmail);

    const partnerName = sig.partnerSignerName || sig.signedByName || null;
    const partnerTitle = sig.partnerSignerTitle || null;
    const partnerEmail = sig.partnerSignerEmail || sig.signedByEmail || null;

    // Derive per-step state from the timestamps + status.
    const sentDone = true;
    const partnerDone = Boolean(sig.partnerSignedAt);
    const tagadaDone = Boolean(sig.tagadaSignedAt);
    const completeDone = status === 'signed';

    const steps: Array<{ key: string; label: string; sub?: string | null; when?: string | Date | null; state: StepState }> = [];
    steps.push({
        key: 'sent',
        label: 'Sent for signature',
        sub: sig.proposedByEmail ? `by ${sig.proposedByEmail}` : null,
        when: sig.proposedAt ?? sig.createdAt,
        state: 'done',
    });
    steps.push({
        key: 'partner',
        label: 'Partner signed',
        sub: partnerName ? `${partnerName}${partnerTitle ? ` · ${partnerTitle}` : ''}` : partnerEmail,
        when: sig.partnerSignedAt,
        state: partnerDone ? 'done' : isTerminalFail ? 'blocked' : 'current',
    });
    if (hasTagadaSigner) {
        steps.push({
            key: 'tagada',
            label: 'TagadaPay counter-signed',
            sub: sig.tagadaSignerName ?? sig.tagadaSignerEmail,
            when: sig.tagadaSignedAt,
            state: tagadaDone
                ? 'done'
                : isTerminalFail
                  ? 'blocked'
                  : partnerDone
                    ? 'current'
                    : 'pending',
        });
    }
    steps.push({
        key: 'complete',
        label: 'Completed & activated',
        when: sig.signedAt ?? sig.effectiveAt,
        state: completeDone ? 'done' : isTerminalFail ? 'blocked' : 'pending',
    });

    const toneBorder: Record<string, string> = {
        amber: 'border-amber-300',
        blue: 'border-blue-300',
        green: 'border-green-300',
        red: 'border-red-300',
        gray: 'border-muted',
    };

    return (
        <Card className={toneBorder[meta.tone] ?? 'border-muted'}>
            <CardHeader>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <CardTitle className="flex items-center gap-2 text-base">
                            <FileSignature className="h-5 w-5" />
                            Signature Progress
                            <StatusBadge status={status} />
                        </CardTitle>
                        <CardDescription>{meta.description}</CardDescription>
                    </div>
                    {isSuperAdmin && isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                </div>
            </CardHeader>
            <CardContent className="space-y-5">
                {/* Step timeline */}
                <ol className="space-y-3">
                    {steps.map((step) => (
                        <li key={step.key} className="flex items-start gap-3">
                            <StepIcon state={step.state} />
                            <div className="flex-1">
                                <div className="flex items-center justify-between gap-2">
                                    <span
                                        className={
                                            step.state === 'pending'
                                                ? 'text-sm text-muted-foreground'
                                                : 'text-sm font-medium'
                                        }
                                    >
                                        {step.label}
                                    </span>
                                    {step.when && (
                                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                                            {fmtDateTime(step.when)}
                                        </span>
                                    )}
                                </div>
                                {step.sub && (
                                    <p className="text-xs text-muted-foreground">{step.sub}</p>
                                )}
                                {step.key === 'partner' && step.state === 'current' && (
                                    <p className="text-xs text-amber-700">
                                        Blocked here: the partner has not signed yet
                                        {partnerEmail ? ` (${partnerEmail})` : ''}.
                                    </p>
                                )}
                                {step.key === 'tagada' && step.state === 'current' && (
                                    <p className="text-xs text-blue-700">
                                        Blocked here: awaiting TagadaPay counter-signature.
                                    </p>
                                )}
                            </div>
                        </li>
                    ))}
                </ol>

                {/* Terminal failure callout */}
                {isTerminalFail && (
                    <Alert className="border-red-300 bg-red-50">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                        <AlertTitle className="text-red-800">{meta.label}</AlertTitle>
                        <AlertDescription className="text-red-700">
                            {meta.description} {isSuperAdmin && 'Propose a new rate to restart the signing flow.'}
                        </AlertDescription>
                    </Alert>
                )}

                {/* Parties */}
                <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-md border p-3">
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Partner signatory</p>
                        <p className="mt-1 text-sm font-medium">{partnerName ?? '—'}</p>
                        {partnerTitle && <p className="text-xs text-muted-foreground">{partnerTitle}</p>}
                        {partnerEmail && <p className="text-xs text-muted-foreground">{partnerEmail}</p>}
                        <p className="mt-1 text-xs">
                            {sig.partnerSignedAt ? (
                                <span className="text-green-700">Signed {fmtDateTime(sig.partnerSignedAt)}</span>
                            ) : (
                                <span className="text-amber-700">Not signed yet</span>
                            )}
                        </p>
                    </div>
                    {hasTagadaSigner && (
                        <div className="rounded-md border p-3">
                            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">TagadaPay counter-signer</p>
                            <p className="mt-1 text-sm font-medium">{sig.tagadaSignerName ?? '—'}</p>
                            {sig.tagadaSignerEmail && <p className="text-xs text-muted-foreground">{sig.tagadaSignerEmail}</p>}
                            <p className="mt-1 text-xs">
                                {sig.tagadaSignedAt ? (
                                    <span className="text-green-700">Counter-signed {fmtDateTime(sig.tagadaSignedAt)}</span>
                                ) : (
                                    <span className="text-muted-foreground">Awaiting partner first</span>
                                )}
                            </p>
                        </div>
                    )}
                </div>

                {/* Superadmin: Firma debugging details + raw recipient events */}
                {isSuperAdmin && (
                    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
                            {sig.firmaStatus && (
                                <span>
                                    Firma status: <span className="font-mono text-foreground">{sig.firmaStatus}</span>
                                </span>
                            )}
                            {sig.firmaContractId && (
                                <span>
                                    Envelope: <span className="font-mono text-foreground">{sig.firmaContractId}</span>
                                </span>
                            )}
                            {sig.firmaSigningUrl && (
                                <a
                                    href={sig.firmaSigningUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                                >
                                    Open signing link <ExternalLink className="h-3 w-3" />
                                </a>
                            )}
                        </div>
                        {Array.isArray(sig.recipientsState) && sig.recipientsState.length > 0 && (
                            <div className="space-y-1">
                                <p className="text-xs font-medium text-muted-foreground">Firma event trail</p>
                                {sig.recipientsState.map((r, i) => (
                                    <div key={i} className="flex items-center justify-between gap-2 text-xs">
                                        <span>
                                            <Badge variant="outline" className="mr-2">{r.party ?? 'party'}</Badge>
                                            <span className="font-mono">{r.firmaEventType ?? 'event'}</span>
                                            {(r.name || r.email) && (
                                                <span className="ml-2 text-muted-foreground">
                                                    {r.name ?? r.email}
                                                </span>
                                            )}
                                        </span>
                                        <span className="text-muted-foreground whitespace-nowrap">{fmtDateTime(r.signedAt)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// ─── View / download contract PDF (signed or sent envelope) ─────────

function ViewContractButton({
    partnerSlug,
    isSuperAdmin,
    versionId,
    label = 'View contract',
}: {
    partnerSlug: string;
    isSuperAdmin: boolean;
    versionId?: string;
    label?: string;
}) {
    const utils = trpc.useUtils();
    const [loading, setLoading] = React.useState(false);

    const handleClick = async () => {
        setLoading(true);
        try {
            const res = isSuperAdmin
                ? await utils.partnerBuyRate.getSignedDocument.fetch({ partnerSlug, versionId })
                : await utils.partnerBuyRate.selfGetSignedDocument.fetch({ partner: partnerSlug });
            if (res?.documentUrl) {
                window.open(res.documentUrl, '_blank', 'noopener,noreferrer');
            } else {
                toast.error('Contract PDF not available yet');
            }
        } catch (err: any) {
            toast.error(err?.message ?? 'Could not load the contract PDF');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button variant="outline" size="sm" disabled={loading} onClick={handleClick}>
            {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
                <ExternalLink className="mr-2 h-4 w-4" />
            )}
            {label}
        </Button>
    );
}

/** @deprecated alias — use ViewContractButton */
const ViewSignedContractButton = ViewContractButton;

// ─── Partner default contract template ────────────────────────────────

function PartnerContractTemplateCard({ partnerSlug }: { partnerSlug: string }) {
    const listQuery = trpc.partnerBuyRate.listContractTemplates.useQuery();
    const assignmentQuery = trpc.partnerBuyRate.getPartnerContractTemplate.useQuery({ partnerSlug });
    const utils = trpc.useUtils();
    const [selectedId, setSelectedId] = React.useState('__global__');

    React.useEffect(() => {
        if (assignmentQuery.data) {
            setSelectedId(assignmentQuery.data.templateId ?? '__global__');
        }
    }, [assignmentQuery.data?.templateId]);

    const setMutation = trpc.partnerBuyRate.setPartnerContractTemplate.useMutation({
        onSuccess: () => {
            toast.success('Partner default contract template updated');
            utils.partnerBuyRate.getPartnerContractTemplate.invalidate({ partnerSlug });
        },
        onError: (err) => toast.error(err.message),
    });

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-base">Default contract template</CardTitle>
                <CardDescription>
                    Pre-selected when proposing a buy rate for this partner. Leave on platform default
                    unless this partner has negotiated clause wording.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {listQuery.isLoading || assignmentQuery.isLoading ? (
                    <Skeleton className="h-10 w-full max-w-md" />
                ) : (
                    <Select
                        value={selectedId}
                        disabled={setMutation.isPending}
                        onValueChange={(value) => {
                            setSelectedId(value);
                            setMutation.mutate({
                                partnerSlug,
                                templateId: value === '__global__' ? null : value,
                            });
                        }}
                    >
                        <SelectTrigger className="max-w-md">
                            <SelectValue placeholder="Select default template" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__global__">Platform default</SelectItem>
                            {(listQuery.data ?? []).map((item) => (
                                <SelectItem key={item.id} value={item.id}>
                                    {item.name}
                                    {item.isDefault ? ' (platform default)' : ''}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </CardContent>
        </Card>
    );
}

// ─── Propose dialog ─────────────────────────────────────────────────

function ProposeDialog({ partnerSlug }: { partnerSlug: string }) {
    const [open, setOpen] = React.useState(false);
    const [tagadaSignerId, setTagadaSignerId] = React.useState('');
    const [note, setNote] = React.useState('');
    const [contractClauses, setContractClauses] = React.useState<ContractClauseTemplate | null>(null);
    const [selectedTemplateId, setSelectedTemplateId] = React.useState<string | null>(null);
    const [clausesDirty, setClausesDirty] = React.useState(false);

    // Existing versions — lets the operator start a new version from a
    // previous one instead of re-entering the whole grid (incremental edits).
    const versionsQuery = trpc.partnerBuyRate.listVersions.useQuery(
        { partnerSlug },
        { enabled: open },
    );
    const [baseVersionId, setBaseVersionId] = React.useState<string>('__standard__');
    const [basePrefilled, setBasePrefilled] = React.useState(false);

    const templatesQuery = trpc.partnerBuyRate.listContractTemplates.useQuery(undefined, {
        enabled: open,
    });
    const partnerTemplateQuery = trpc.partnerBuyRate.getPartnerContractTemplate.useQuery(
        { partnerSlug },
        { enabled: open },
    );
    const templateDetailQuery = trpc.partnerBuyRate.getContractTemplateById.useQuery(
        { id: selectedTemplateId! },
        { enabled: open && !!selectedTemplateId },
    );

    React.useEffect(() => {
        if (!open || selectedTemplateId || !templatesQuery.data?.length) return;
        const assigned = partnerTemplateQuery.data?.templateId;
        const defaultId =
            templatesQuery.data.find((t) => t.isDefault)?.id ?? templatesQuery.data[0]?.id;
        setSelectedTemplateId(assigned ?? defaultId ?? null);
    }, [open, selectedTemplateId, templatesQuery.data, partnerTemplateQuery.data?.templateId]);

    React.useEffect(() => {
        if (!open || !templateDetailQuery.data?.template || clausesDirty) return;
        setContractClauses(
            structuredClone(templateDetailQuery.data.template) as ContractClauseTemplate,
        );
    }, [open, templateDetailQuery.data?.template, clausesDirty]);

    React.useEffect(() => {
        if (!open) {
            setContractClauses(null);
            setSelectedTemplateId(null);
            setClausesDirty(false);
            setBaseVersionId('__standard__');
            setBasePrefilled(false);
            setGrid(makeStandardGrid());
        }
    }, [open]);

    // The partner's signatory is its verified contract signer, read by the
    // server (TAG-3531); when the partner is not verified yet, the step that
    // blocks the contract.
    const contractSignerQuery = trpc.partnerBuyRate.contractSigner.useQuery(
        { partnerSlug },
        { enabled: open },
    );
    const contractSigner = contractSignerQuery.data;

    // Authorised Tagada counter-signers — operator chooses who counter-signs.
    const tagadaSignersQuery = trpc.partnerBuyRate.listTagadaSigners.useQuery();
    const tagadaSigners = tagadaSignersQuery.data ?? [];

    React.useEffect(() => {
        if (!tagadaSignerId && tagadaSigners.length > 0) {
            setTagadaSignerId(tagadaSigners[0].id);
        }
    }, [tagadaSigners, tagadaSignerId]);

    // Pre-load Tagada's standard Buy Rate so the operator doesn't have to
    // re-enter the whole grid. They can edit any line or reset below.
    const [grid, setGrid] = React.useState<Record<string, any>>(makeStandardGrid);

    // Copy a previous version's grid + clauses into the dialog so a new
    // version can be edited incrementally from it.
    const applyBaseVersion = React.useCallback(
        (id: string) => {
            setBaseVersionId(id);
            if (id === '__standard__') {
                setGrid(makeStandardGrid());
                // Let the template flow reload the clause library defaults.
                setClausesDirty(false);
                if (templateDetailQuery.data?.template) {
                    setContractClauses(
                        structuredClone(templateDetailQuery.data.template) as ContractClauseTemplate,
                    );
                }
                return;
            }
            const version = (versionsQuery.data ?? []).find((v: any) => v.id === id);
            if (!version) return;
            setGrid(structuredClone(version.grid) as Record<string, any>);
            if (version.contractClauses) {
                setContractClauses(
                    structuredClone(version.contractClauses) as ContractClauseTemplate,
                );
                setClausesDirty(true);
            }
        },
        [versionsQuery.data, templateDetailQuery.data?.template],
    );

    // On open, default to the most recent version (if any) so the operator
    // starts from what was last proposed instead of the blank standard grid.
    React.useEffect(() => {
        if (!open || basePrefilled || !versionsQuery.data) return;
        setBasePrefilled(true);
        const latest = versionsQuery.data[0] as any;
        if (latest) applyBaseVersion(latest.id);
    }, [open, basePrefilled, versionsQuery.data, applyBaseVersion]);

    const utils = trpc.useUtils();
    const proposeMutation = trpc.partnerBuyRate.proposeAndSend.useMutation({
        onSuccess: () => {
            toast.success('Buy rate proposed and sent for signature');
            setOpen(false);
            utils.partnerBuyRate.listVersions.invalidate({ partnerSlug });
            utils.partnerBuyRate.listVersionHistory.invalidate({ partnerSlug });
            utils.partnerBuyRate.getPendingSignature.invalidate({ partnerSlug });
            utils.partnerBuyRate.getLatestSignature.invalidate({ partnerSlug });
            utils.partnerBuyRate.listEvents.invalidate({ partnerSlug });
        },
        onError: (err) => toast.error(err.message),
    });

    const proposeOnlyMutation = trpc.partnerBuyRate.propose.useMutation({
        onSuccess: () => {
            toast.success('Buy rate draft created');
            setOpen(false);
            utils.partnerBuyRate.listVersions.invalidate({ partnerSlug });
            utils.partnerBuyRate.listVersionHistory.invalidate({ partnerSlug });
            utils.partnerBuyRate.listEvents.invalidate({ partnerSlug });
        },
        onError: (err) => toast.error(err.message),
    });

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm">
                    <Plus className="mr-2 h-4 w-4" /> Propose New Rate
                </Button>
            </DialogTrigger>
            <DialogContent className="flex h-[min(96dvh,calc(100dvh-1rem))] w-[min(1400px,calc(100vw-1.5rem))] max-w-[calc(100vw-1.5rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[calc(100vw-1.5rem)]">
                <div className="shrink-0 border-b px-6 py-5">
                    <DialogHeader>
                        <DialogTitle>Propose New CRM Buy Rate</DialogTitle>
                        <DialogDescription>
                            Configure pricing, contract clauses, and signatories. Save as draft or send
                            immediately for signature via Firma.
                        </DialogDescription>
                    </DialogHeader>
                </div>

                <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(280px,360px)_1fr]">
                    <aside className="space-y-4 overflow-y-auto border-b bg-muted/20 px-6 py-5 lg:border-b-0 lg:border-r">
                        <div>
                            <h3 className="text-sm font-semibold">Partner signatory</h3>
                            <p className="text-xs text-muted-foreground">
                                The partner's verified contract signer, printed on the contract and shown on the
                            Firma envelope. Compliance changes it, not this dialog.
                            </p>
                        </div>
                        {contractSignerQuery.isLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : contractSigner?.ok ? (
                            <div className="space-y-1 rounded-md border bg-background px-3 py-2">
                                <p className="text-sm font-medium">{contractSigner.signer.name}</p>
                                <p className="text-xs text-muted-foreground">{contractSigner.signer.title}</p>
                                <p className="text-xs text-muted-foreground">{contractSigner.signer.email}</p>
                            </div>
                        ) : (
                            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                {(contractSigner && !contractSigner.ok ? contractSigner.message : undefined) ?? contractSignerQuery.error?.message ?? 'The partner signatory could not be read.'}
                            </p>
                        )}

                        <Separator />

                        <div className="space-y-2">
                            <Label>TagadaPay counter-signer</Label>
                            <Select value={tagadaSignerId} onValueChange={setTagadaSignerId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select who signs on Tagada's behalf" />
                                </SelectTrigger>
                                <SelectContent>
                                    {tagadaSigners.map((s) => (
                                        <SelectItem key={s.id} value={s.id}>
                                            {s.name} — {s.title} ({s.email})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                                The partner signs first, then this person counter-signs for TagadaPay.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label>Note (optional)</Label>
                            <Input
                                placeholder="Initial buy rate proposal..."
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                            />
                        </div>
                    </aside>

                    <div className="flex min-h-0 flex-col">
                        <Tabs defaultValue="grid" className="flex min-h-0 flex-1 flex-col">
                            <TabsList className="mx-6 mt-5 w-fit shrink-0">
                                <TabsTrigger value="grid">Fee grid</TabsTrigger>
                                <TabsTrigger value="clauses">Contract clauses</TabsTrigger>
                            </TabsList>
                            <TabsContent
                                value="grid"
                                className="mt-0 min-h-0 flex-1 space-y-3 overflow-y-auto px-6 pb-6 pt-4 data-[state=inactive]:hidden"
                            >
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <h4 className="text-sm font-medium">Fee Grid</h4>
                                        <p className="text-xs text-muted-foreground">
                                            Pre-filled from the selected base version. Edit any line before sending.
                                        </p>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-2">
                                        <Label className="text-xs text-muted-foreground">Start from</Label>
                                        <Select value={baseVersionId} onValueChange={applyBaseVersion}>
                                            <SelectTrigger className="h-8 w-[240px]">
                                                <SelectValue placeholder="Base version" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__standard__">
                                                    Standard Tagada Buy Rate
                                                </SelectItem>
                                                {(versionsQuery.data ?? []).map((v: any) => (
                                                    <SelectItem key={v.id} value={v.id}>
                                                        v{v.version} — {v.status}
                                                        {v.proposedAt
                                                            ? ` (${new Date(v.proposedAt).toLocaleDateString()})`
                                                            : ''}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <OmittedLinesWarning grid={grid} />
                                <GridEditor grid={grid} onChange={setGrid} />
                            </TabsContent>
                            <TabsContent
                                value="clauses"
                                className="mt-0 min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6 pt-4 data-[state=inactive]:hidden"
                            >
                                <div className="space-y-2">
                                    <Label>Contract template</Label>
                                    <Select
                                        value={selectedTemplateId ?? ''}
                                        onValueChange={(id) => {
                                            setSelectedTemplateId(id);
                                            setClausesDirty(false);
                                        }}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select a template" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {(templatesQuery.data ?? []).map((item) => (
                                                <SelectItem key={item.id} value={item.id}>
                                                    {item.name}
                                                    {item.isDefault ? ' (platform default)' : ''}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-muted-foreground">
                                        Loads clause text from the library. Edits apply only to this proposal.
                                    </p>
                                </div>
                                {templateDetailQuery.isLoading || !contractClauses ? (
                                    <div className="space-y-2">
                                        {[...Array(3)].map((_, i) => (
                                            <Skeleton key={i} className="h-24 w-full" />
                                        ))}
                                    </div>
                                ) : (
                                    <ClauseEditor
                                        template={contractClauses}
                                        onChange={(next) => {
                                            setContractClauses(next);
                                            setClausesDirty(true);
                                        }}
                                        onReset={() => {
                                            if (templateDetailQuery.data?.template) {
                                                setContractClauses(
                                                    structuredClone(
                                                        templateDetailQuery.data.template,
                                                    ) as ContractClauseTemplate,
                                                );
                                                setClausesDirty(false);
                                            }
                                        }}
                                    />
                                )}
                            </TabsContent>
                        </Tabs>
                    </div>
                </div>

                <DialogFooter className="shrink-0 gap-2 border-t px-6 py-4 sm:justify-end">
                    <Button
                        variant="outline"
                        disabled={proposeOnlyMutation.isPending}
                        onClick={() =>
                            proposeOnlyMutation.mutate({
                                partnerSlug,
                                grid: grid as any,
                                note: note || undefined,
                                contractClauses: contractClauses ?? undefined,
                                contractTemplateId: selectedTemplateId ?? undefined,
                            })
                        }
                    >
                        {proposeOnlyMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save Draft
                    </Button>
                    <Button
                        disabled={
                            !contractSigner?.ok ||
                            !tagadaSignerId ||
                            !contractClauses ||
                            proposeMutation.isPending
                        }
                        onClick={() =>
                            proposeMutation.mutate({
                                partnerSlug,
                                grid: grid as any,
                                note: note || undefined,
                                tagadaSignerId,
                                contractClauses: contractClauses ?? undefined,
                                contractTemplateId: selectedTemplateId ?? undefined,
                            })
                        }
                    >
                        {proposeMutation.isPending ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="mr-2 h-4 w-4" />
                        )}
                        Send for Signature
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── Grid editor (per-category fee inputs) ──────────────────────────

const EDITABLE_CATEGORIES: { key: CategoryKey; label: string; type: CategoryType }[] = CATEGORIES;

/**
 * Warns the operator about every fee line left unchecked. An omitted line
 * is NOT free for the partner: if it still gets billed to a merchant
 * (e.g. Apple Pay), 100% of the billed amount goes to Tagada by default —
 * equivalent to a 100% Tagada buy rate with 0% partner markup.
 */
function OmittedLinesWarning({ grid }: { grid: Record<string, any> }) {
    // Rx is opt-in (it opens Tagada Rx for the partner), not a billed line
    // that would fall back to Tagada: never flag it as omitted.
    const omitted = EDITABLE_CATEGORIES.filter((cat) => cat.type !== 'rx' && !grid[cat.key]);
    if (omitted.length === 0) return null;

    return (
        <Alert className="border-amber-300 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-800">
                {omitted.length} {omitted.length === 1 ? 'line is' : 'lines are'} not included in
                this buy rate
            </AlertTitle>
            <AlertDescription className="space-y-1 text-amber-700">
                <p className="font-medium">
                    {omitted.map((cat) => cat.label).join(', ')}
                </p>
                <p>
                    An omitted line is not part of the partner's package — but it is not free.
                    If the partner still bills one of these lines to a merchant, 100% of the
                    amount charged goes to Tagada by default: omitting a line is equivalent to
                    a 100% Tagada buy rate (0% partner markup) on it.
                </p>
            </AlertDescription>
        </Alert>
    );
}

function GridEditor({
    grid,
    onChange,
}: {
    grid: Record<string, any>;
    onChange: (g: Record<string, any>) => void;
}) {
    const toggleCategory = (key: string, enabled: boolean) => {
        if (enabled) {
            const cat = EDITABLE_CATEGORIES.find((c) => c.key === key);
            if (!cat) return;
            let defaultVal: any;
            if (cat.type === 'weeklyBase') defaultVal = { enabled: true, amountCents: 0, minApprovedTxns: 1 };
            else if (cat.type === 'fixedPlusBps') defaultVal = { enabled: true, fixedCents: 0, bps: 0 };
            else if (cat.type === 'bpsOnly') defaultVal = { enabled: true, bps: 0 };
            else if (cat.type === 'rx') defaultVal = { ...DEFAULT_RX_TRANSACTION_LINE };
            else defaultVal = { enabled: true, fixedCents: 0 };
            onChange({ ...grid, [key]: defaultVal });
        } else {
            const next = { ...grid };
            delete next[key];
            onChange(next);
        }
    };

    const updateField = (key: string, field: string, value: number | boolean) => {
        onChange({
            ...grid,
            [key]: { ...grid[key], [field]: value },
        });
    };

    return (
        <div className="space-y-2">
            {EDITABLE_CATEGORIES.map((cat) => {
                const active = !!grid[cat.key];
                return (
                    <div
                        key={cat.key}
                        className={`flex items-center gap-3 rounded-md border p-2 text-sm ${
                            active ? 'bg-background' : 'bg-muted/30'
                        }`}
                    >
                        <input
                            type="checkbox"
                            checked={active}
                            onChange={(e) => toggleCategory(cat.key, e.target.checked)}
                            className="h-4 w-4"
                        />
                        <span className="w-40 font-medium">{cat.label}</span>
                        {!active && cat.type === 'rx' && (
                            <span className="text-xs text-muted-foreground">
                                Not included: this partner can't offer Tagada Rx
                            </span>
                        )}
                        {!active && cat.type !== 'rx' && (
                            <span className="flex items-center gap-1 text-xs text-amber-600">
                                <AlertTriangle className="h-3 w-3 shrink-0" />
                                Not included — if billed anyway, 100% goes to Tagada
                            </span>
                        )}
                        {active && cat.type === 'weeklyBase' && (
                            <div className="flex items-center gap-2">
                                <Label className="text-xs">Cents:</Label>
                                <Input
                                    type="number"
                                    className="h-7 w-20"
                                    value={grid[cat.key]?.amountCents ?? 0}
                                    onChange={(e) => updateField(cat.key, 'amountCents', Number(e.target.value))}
                                    min={0}
                                />
                            </div>
                        )}
                        {active && cat.type === 'fixedPlusBps' && (
                            <div className="flex items-center gap-2">
                                <Label className="text-xs">Fixed (¢):</Label>
                                <Input
                                    type="number"
                                    className="h-7 w-20"
                                    value={grid[cat.key]?.fixedCents ?? 0}
                                    onChange={(e) => updateField(cat.key, 'fixedCents', Number(e.target.value))}
                                    min={0}
                                />
                                <Label className="text-xs">Bps:</Label>
                                <Input
                                    type="number"
                                    className="h-7 w-20"
                                    value={grid[cat.key]?.bps ?? 0}
                                    onChange={(e) => updateField(cat.key, 'bps', Number(e.target.value))}
                                    min={0}
                                    max={10000}
                                />
                            </div>
                        )}
                        {active && cat.type === 'bpsOnly' && (
                            <div className="flex items-center gap-2">
                                <Label className="text-xs">Bps:</Label>
                                <Input
                                    type="number"
                                    className="h-7 w-20"
                                    value={grid[cat.key]?.bps ?? 0}
                                    onChange={(e) => updateField(cat.key, 'bps', Number(e.target.value))}
                                    min={0}
                                    max={10000}
                                />
                            </div>
                        )}
                        {active && cat.type === 'flat' && (
                            <div className="flex items-center gap-2">
                                <Label className="text-xs">Fixed (¢):</Label>
                                <Input
                                    type="number"
                                    className="h-7 w-20"
                                    value={grid[cat.key]?.fixedCents ?? 0}
                                    onChange={(e) => updateField(cat.key, 'fixedCents', Number(e.target.value))}
                                    min={0}
                                />
                            </div>
                        )}
                        {active && cat.type === 'rx' && (
                            <div className="flex items-center gap-2">
                                <Label className="text-xs">Bps:</Label>
                                <Input
                                    type="number"
                                    className="h-7 w-20"
                                    value={grid[cat.key]?.bps ?? 0}
                                    onChange={(e) => updateField(cat.key, 'bps', Number(e.target.value))}
                                    min={0}
                                    max={10000}
                                />
                                <label className="ml-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <input
                                        type="checkbox"
                                        className="h-3.5 w-3.5"
                                        checked={!!grid[cat.key]?.allowBelowPublic}
                                        onChange={(e) => updateField(cat.key, 'allowBelowPublic', e.target.checked)}
                                    />
                                    Allow merchant price below {formatBps(RX_PUBLIC_SELL_BPS)}
                                </label>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ─── Markup editor ──────────────────────────────────────────────────

function MarkupEditor({
    partnerSlug,
    currentMarkup,
    buyRateGrid,
    isSuperAdmin,
}: {
    partnerSlug: string;
    currentMarkup?: Record<string, any> | null;
    buyRateGrid: Record<string, any>;
    isSuperAdmin: boolean;
}) {
    const [markup, setMarkup] = React.useState<Record<string, any>>(currentMarkup ?? {});
    const [dirty, setDirty] = React.useState(false);
    const [migrationOpen, setMigrationOpen] = React.useState(false);

    React.useEffect(() => {
        if (currentMarkup) {
            setMarkup(currentMarkup);
            setDirty(false);
        }
    }, [currentMarkup]);

    const utils = trpc.useUtils();

    const adminSaveMutation = trpc.partnerBuyRate.setMarkup.useMutation({
        onSuccess: () => {
            toast.success('Markup saved');
            setDirty(false);
            utils.partnerBuyRate.getMarkup.invalidate({ partnerSlug });
            utils.partnerBuyRate.getActive.invalidate({ partnerSlug });
            utils.partnerBuyRate.listEvents.invalidate({ partnerSlug });
        },
        onError: (err: any) => toast.error(err.message),
    });

    const selfSaveMutation = trpc.partnerBuyRate.selfSetMarkup.useMutation({
        onSuccess: () => {
            toast.success('Markup saved');
            setDirty(false);
            utils.partnerBuyRate.selfGetMarkup.invalidate({ partner: partnerSlug });
            utils.partnerBuyRate.selfGetActiveBuyRate.invalidate({ partner: partnerSlug });
        },
        onError: (err: any) => toast.error(err.message),
    });

    const saveMutation = isSuperAdmin ? adminSaveMutation : selfSaveMutation;

    const updateMarkupField = (key: string, field: string, value: number) => {
        setMarkup((prev) => ({
            ...prev,
            [key]: { ...(prev[key] ?? {}), [field]: value },
        }));
        setDirty(true);
    };

    const enabledCategories = CATEGORIES.filter((c) => buyRateGrid[c.key]?.enabled !== false && buyRateGrid[c.key]);

    return (
        <>
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                        <CardTitle className="text-base">
                            {isSuperAdmin ? 'Partner Markup' : 'Your Markup'}
                        </CardTitle>
                        <CardDescription>
                            {isSuperAdmin
                                ? "The partner's margin on top of the Tagada buy rate. The merchant sees only the total (sell rate)."
                                : 'Add your margin on top of the Tagada buy rate. Your merchants will see only the total price.'}
                        </CardDescription>
                        </div>
                        <div className="flex gap-2">
                            {isSuperAdmin ? (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setMigrationOpen(true)}
                                >
                                    <Zap className="mr-2 h-4 w-4" />
                                    Apply to All Merchants
                                </Button>
                            ) : (
                                <PartnerApplyButton partnerSlug={partnerSlug} />
                            )}
                            <Button
                                size="sm"
                                disabled={!dirty || saveMutation.isPending}
                                onClick={() =>
                                    isSuperAdmin
                                        ? adminSaveMutation.mutate({ partnerSlug, markup: markup as any })
                                        : selfSaveMutation.mutate({ partner: partnerSlug, markup: markup as any })
                                }
                            >
                                {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Save Markup
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        {enabledCategories.map((cat) => (
                            <div key={cat.key} className="flex items-center gap-3 rounded-md border p-2 text-sm">
                                <span className="w-40 font-medium">{cat.label}</span>
                                {cat.type === 'weeklyBase' && (
                                    <div className="flex items-center gap-2">
                                        <Label className="text-xs">+ Cents:</Label>
                                        <Input
                                            type="number"
                                            className="h-7 w-20"
                                            value={markup[cat.key]?.amountCents ?? 0}
                                            onChange={(e) =>
                                                updateMarkupField(cat.key, 'amountCents', Number(e.target.value))
                                            }
                                            min={0}
                                        />
                                    </div>
                                )}
                                {cat.type === 'fixedPlusBps' && (
                                    <div className="flex items-center gap-2">
                                        <Label className="text-xs">+ Fixed (¢):</Label>
                                        <Input
                                            type="number"
                                            className="h-7 w-20"
                                            value={markup[cat.key]?.fixedCents ?? 0}
                                            onChange={(e) =>
                                                updateMarkupField(cat.key, 'fixedCents', Number(e.target.value))
                                            }
                                            min={0}
                                        />
                                        <Label className="text-xs">+ Bps:</Label>
                                        <Input
                                            type="number"
                                            className="h-7 w-20"
                                            value={markup[cat.key]?.bps ?? 0}
                                            onChange={(e) =>
                                                updateMarkupField(cat.key, 'bps', Number(e.target.value))
                                            }
                                            min={0}
                                            max={10000}
                                        />
                                    </div>
                                )}
                                {(cat.type === 'bpsOnly' || cat.type === 'rx') && (
                                    <div className="flex items-center gap-2">
                                        <Label className="text-xs">+ Bps:</Label>
                                        <Input
                                            type="number"
                                            className="h-7 w-20"
                                            value={markup[cat.key]?.bps ?? 0}
                                            onChange={(e) =>
                                                updateMarkupField(cat.key, 'bps', Number(e.target.value))
                                            }
                                            min={0}
                                            max={10000}
                                        />
                                        {cat.type === 'rx' && (
                                            <span className="text-xs text-muted-foreground">
                                                Merchant price{' '}
                                                {formatBps(resolveRxSellBps(buyRateGrid[cat.key], markup[cat.key]?.bps ?? 0))}
                                                {buyRateGrid[cat.key]?.allowBelowPublic
                                                    ? ''
                                                    : ` (never below ${formatBps(RX_PUBLIC_SELL_BPS)})`}
                                            </span>
                                        )}
                                    </div>
                                )}
                                {cat.type === 'flat' && (
                                    <div className="flex items-center gap-2">
                                        <Label className="text-xs">+ Fixed (¢):</Label>
                                        <Input
                                            type="number"
                                            className="h-7 w-20"
                                            value={markup[cat.key]?.fixedCents ?? 0}
                                            onChange={(e) =>
                                                updateMarkupField(cat.key, 'fixedCents', Number(e.target.value))
                                            }
                                            min={0}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {isSuperAdmin && migrationOpen && (
                <MigrationRecapDialog
                    partnerSlug={partnerSlug}
                    open={migrationOpen}
                    onOpenChange={setMigrationOpen}
                />
            )}
        </>
    );
}

// ─── Partner Apply Button (self-service) ─────────────────────────────

function PartnerApplyButton({ partnerSlug }: { partnerSlug: string }) {
    const [confirm, setConfirm] = React.useState(false);
    const utils = trpc.useUtils();

    const applyMutation = trpc.partnerBuyRate.selfBulkApplyMarkup.useMutation({
        onSuccess: (data) => {
            toast.success(`Markup applied to ${(data as any)?.updated ?? 0} merchants`);
            utils.partnerBuyRate.selfGetMarkup.invalidate({ partner: partnerSlug });
        },
        onError: (err) => toast.error(err.message),
    });

    return (
        <>
            <Button
                variant="outline"
                size="sm"
                disabled={applyMutation.isPending}
                onClick={() => setConfirm(true)}
            >
                {applyMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                    <Zap className="mr-2 h-4 w-4" />
                )}
                Apply to My Merchants
            </Button>

            <AlertDialog open={confirm} onOpenChange={setConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Apply markup to all merchants?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will update the CRM billing fees for all your merchants
                            to reflect the current buy rate + your markup. Merchants will
                            see the new total pricing on their next billing cycle.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                setConfirm(false);
                                applyMutation.mutate({ partner: partnerSlug });
                            }}
                        >
                            Confirm Apply
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

// ─── Migration Recap Dialog ─────────────────────────────────────────

function MigrationRecapDialog({
    partnerSlug,
    open,
    onOpenChange,
}: {
    partnerSlug: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    const [confirmAction, setConfirmAction] = React.useState<'crm_only' | 'crm_and_pricing' | null>(null);

    const previewQuery = trpc.partnerBuyRate.previewMigration.useQuery(
        { partnerSlug },
        { enabled: open },
    );

    const utils = trpc.useUtils();
    const applyMutation = trpc.partnerBuyRate.applyMigration.useMutation({
        onSuccess: (data) => {
            const msgs: string[] = [];
            if (data.crmFeesApplied > 0) msgs.push(`CRM fees updated for ${data.crmFeesApplied} merchants`);
            if (data.pricingGroupsReset > 0) msgs.push(`${data.pricingGroupsReset} Stripe pricing groups reset`);
            if (data.pricingGroupErrors.length > 0) {
                msgs.push(`${data.pricingGroupErrors.length} pricing group errors`);
            }
            toast.success(msgs.join('. ') || 'Migration applied');
            utils.partnerBuyRate.listEvents.invalidate({ partnerSlug });
            onOpenChange(false);
        },
        onError: (err) => toast.error(err.message),
    });

    const preview = previewQuery.data;
    const changedCount = preview?.merchants.filter((m) => m.feesChanged).length ?? 0;
    const unchangedCount = (preview?.merchantCount ?? 0) - changedCount;

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Zap className="h-5 w-5" />
                            Apply to All Merchants — Impact Preview
                        </DialogTitle>
                        <DialogDescription>
                            Review the changes before applying. This will update CRM billing fees
                            for all merchants under this partner.
                        </DialogDescription>
                    </DialogHeader>

                    {previewQuery.isLoading ? (
                        <div className="space-y-3 py-4">
                            <Skeleton className="h-8 w-full" />
                            <Skeleton className="h-32 w-full" />
                            <Skeleton className="h-32 w-full" />
                        </div>
                    ) : previewQuery.error ? (
                        <Alert variant="destructive">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertTitle>Cannot generate preview</AlertTitle>
                            <AlertDescription>{previewQuery.error.message}</AlertDescription>
                        </Alert>
                    ) : preview ? (
                        <div className="space-y-4">
                            {/* Summary cards */}
                            <div className="grid grid-cols-3 gap-3">
                                <div className="rounded-lg border p-3 text-center">
                                    <p className="text-2xl font-bold">{preview.merchantCount}</p>
                                    <p className="text-xs text-muted-foreground">Total Merchants</p>
                                </div>
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
                                    <p className="text-2xl font-bold text-amber-700">{changedCount}</p>
                                    <p className="text-xs text-amber-600">Fees Will Change</p>
                                </div>
                                <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-center">
                                    <p className="text-2xl font-bold text-green-700">{unchangedCount}</p>
                                    <p className="text-xs text-green-600">Already Up to Date</p>
                                </div>
                            </div>

                            {/* Stripe pricing group warning */}
                            {preview.hasCustomPricingGroup && (
                                <Alert className="border-orange-300 bg-orange-50">
                                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                                    <AlertTitle className="text-orange-800">
                                        Custom Stripe Pricing Group Detected
                                    </AlertTitle>
                                    <AlertDescription className="text-orange-700">
                                        This partner uses a custom Stripe pricing group:{' '}
                                        <code className="rounded bg-orange-100 px-1 py-0.5 text-xs font-mono">
                                            {preview.currentStripePricingGroup}
                                        </code>
                                        . You can choose to reset it to the platform standard (
                                        <code className="rounded bg-orange-100 px-1 py-0.5 text-xs font-mono">
                                            {preview.standardStripePricingGroup}
                                        </code>
                                        ) when applying.
                                    </AlertDescription>
                                </Alert>
                            )}

                            {/* Per-merchant diff table */}
                            <Tabs defaultValue="changed">
                                <TabsList>
                                    <TabsTrigger value="changed">
                                        Changed ({changedCount})
                                    </TabsTrigger>
                                    <TabsTrigger value="all">
                                        All ({preview.merchantCount})
                                    </TabsTrigger>
                                </TabsList>
                                <TabsContent value="changed" className="mt-3">
                                    <MerchantDiffTable
                                        merchants={preview.merchants.filter((m) => m.feesChanged)}
                                    />
                                </TabsContent>
                                <TabsContent value="all" className="mt-3">
                                    <MerchantDiffTable merchants={preview.merchants} />
                                </TabsContent>
                            </Tabs>
                        </div>
                    ) : null}

                    <DialogFooter className="flex-col gap-2 sm:flex-row">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={!preview || changedCount === 0 || applyMutation.isPending}
                            onClick={() => setConfirmAction('crm_only')}
                        >
                            {applyMutation.isPending && confirmAction === 'crm_only' && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Apply CRM Fees Only
                        </Button>
                        {preview?.hasCustomPricingGroup && (
                            <Button
                                variant="destructive"
                                disabled={!preview || applyMutation.isPending}
                                onClick={() => setConfirmAction('crm_and_pricing')}
                            >
                                {applyMutation.isPending && confirmAction === 'crm_and_pricing' && (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                )}
                                Apply CRM + Reset Pricing Group
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirmation dialogs */}
            <AlertDialog
                open={confirmAction === 'crm_only'}
                onOpenChange={(v) => !v && setConfirmAction(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm: Apply CRM Fees Only</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will update the <code>account_plans</code> CRM fees for{' '}
                            <strong>{changedCount} merchant(s)</strong> to match the current
                            buy rate + markup. Stripe pricing groups will <strong>not</strong> be
                            changed.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                setConfirmAction(null);
                                applyMutation.mutate({
                                    partnerSlug,
                                    resetPricingGroup: false,
                                });
                            }}
                        >
                            Confirm Apply
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
                open={confirmAction === 'crm_and_pricing'}
                onOpenChange={(v) => !v && setConfirmAction(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm: Apply CRM + Reset Stripe Pricing</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2">
                            <p>This will perform <strong>two actions</strong>:</p>
                            <ol className="ml-4 list-decimal space-y-1 text-sm">
                                <li>
                                    Update CRM fees in <code>account_plans</code> for{' '}
                                    <strong>{changedCount} merchant(s)</strong>.
                                </li>
                                <li>
                                    Reset the Stripe <code>payments_pricing</code> group from{' '}
                                    <code className="text-xs">{preview?.currentStripePricingGroup}</code>
                                    {' '}to the platform standard{' '}
                                    <code className="text-xs">{preview?.standardStripePricingGroup}</code>
                                    {' '}for all Stripe Connect sub-accounts under this partner.
                                </li>
                            </ol>
                            <p className="font-medium text-destructive">
                                This will change processing fees for merchants on Stripe.
                                This action cannot be easily reversed.
                            </p>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => {
                                setConfirmAction(null);
                                applyMutation.mutate({
                                    partnerSlug,
                                    resetPricingGroup: true,
                                });
                            }}
                        >
                            Confirm Apply + Reset Pricing
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

// ─── Merchant Diff Table ────────────────────────────────────────────

const DIFF_FEE_LABELS: { key: string; label: string; format: 'cents' | 'pct' }[] = [
    { key: 'amountPlan', label: 'Weekly Base', format: 'cents' },
    { key: 'amountPerSuccessTransaction', label: 'Approved (fixed)', format: 'cents' },
    { key: 'pctPerSuccessTransaction', label: 'Approved (%)', format: 'pct' },
    { key: 'amountPerDeclinedTransaction', label: 'Declined (fixed)', format: 'cents' },
    { key: 'pctPerDeclinedTransaction', label: 'Declined (%)', format: 'pct' },
    { key: 'amountPerRefund', label: 'Refund (fixed)', format: 'cents' },
    { key: 'pctPerRefund', label: 'Refund (%)', format: 'pct' },
    { key: 'amountPer3dsVerification', label: '3DS (fixed)', format: 'cents' },
    { key: 'pctPer3dsVerification', label: '3DS (%)', format: 'pct' },
    { key: 'amountPerChargeBack', label: 'Chargeback (fixed)', format: 'cents' },
    { key: 'pctPerChargeBack', label: 'Chargeback (%)', format: 'pct' },
    { key: 'amountPerSubclubTransaction', label: 'SubClub (fixed)', format: 'cents' },
    { key: 'pctPerSubclubTransaction', label: 'SubClub (%)', format: 'pct' },
    { key: 'amountPerLatePayment', label: 'Late Payment (fixed)', format: 'cents' },
    { key: 'pctPerLatePayment', label: 'Late Payment (%)', format: 'pct' },
    { key: 'pctPerAutomaticRetry', label: 'Auto Retry (%)', format: 'pct' },
    { key: 'pctPerUpsell', label: 'Upsell (%)', format: 'pct' },
    { key: 'pctPerSubscription', label: 'Subscription (%)', format: 'pct' },
    { key: 'pctPerApplePay', label: 'Apple Pay (%)', format: 'pct' },
    { key: 'pctPerGooglePay', label: 'Google Pay (%)', format: 'pct' },
    { key: 'amountPerApplePayDomain', label: 'Apple Pay Domain', format: 'cents' },
    { key: 'amountPerCustomDomain', label: 'Custom Domain', format: 'cents' },
];

function formatDiffValue(value: unknown, format: 'cents' | 'pct'): string {
    if (value == null) return '—';
    const num = Number(value);
    if (format === 'cents') return `$${(num / 100).toFixed(2)}`;
    return `${(num / 100).toFixed(2)}%`;
}

function MerchantDiffTable({
    merchants,
}: {
    merchants: Array<{
        accountId: string;
        accountName: string;
        currentFees: Record<string, unknown> | null;
        newFees: Record<string, unknown>;
        feesChanged: boolean;
    }>;
}) {
    const [expanded, setExpanded] = React.useState<string | null>(null);

    if (merchants.length === 0) {
        return (
            <p className="py-4 text-center text-sm text-muted-foreground">
                No merchants with fee changes.
            </p>
        );
    }

    return (
        <div className="max-h-[400px] overflow-auto rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[200px]">Merchant</TableHead>
                        <TableHead className="w-[100px]">Status</TableHead>
                        <TableHead>Key Fees (Current → New)</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {merchants.map((m) => {
                        const isExpanded = expanded === m.accountId;
                        const approvedCurrentFixed = m.currentFees?.amountPerSuccessTransaction;
                        const approvedNewFixed = m.newFees.amountPerSuccessTransaction;
                        const approvedCurrentPct = m.currentFees?.pctPerSuccessTransaction;
                        const approvedNewPct = m.newFees.pctPerSuccessTransaction;

                        return (
                            <React.Fragment key={m.accountId}>
                                <TableRow
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => setExpanded(isExpanded ? null : m.accountId)}
                                >
                                    <TableCell className="font-medium">
                                        <div className="flex items-center gap-1">
                                            <span className={isExpanded ? 'rotate-90' : ''}>
                                                <ArrowRight className="h-3 w-3 transition-transform" />
                                            </span>
                                            <span className="truncate max-w-[180px]">{m.accountName}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {m.feesChanged ? (
                                            <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                                                Changed
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline" className="text-green-700">
                                                <CheckCircle2 className="mr-1 h-3 w-3" />
                                                OK
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="font-mono text-xs">
                                        {m.currentFees ? (
                                            <span>
                                                Approved: {formatDiffValue(approvedCurrentFixed, 'cents')}
                                                {' + '}
                                                {formatDiffValue(approvedCurrentPct, 'pct')}
                                                {m.feesChanged && (
                                                    <>
                                                        <ArrowRight className="mx-1 inline h-3 w-3 text-muted-foreground" />
                                                        {formatDiffValue(approvedNewFixed, 'cents')}
                                                        {' + '}
                                                        {formatDiffValue(approvedNewPct, 'pct')}
                                                    </>
                                                )}
                                            </span>
                                        ) : (
                                            <span className="text-muted-foreground">No current plan</span>
                                        )}
                                    </TableCell>
                                </TableRow>
                                {isExpanded && (
                                    <TableRow>
                                        <TableCell colSpan={3} className="bg-muted/30 p-0">
                                            <div className="p-3">
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead className="h-8 text-xs">Fee</TableHead>
                                                            <TableHead className="h-8 text-xs">Current</TableHead>
                                                            <TableHead className="h-8 text-xs">New</TableHead>
                                                            <TableHead className="h-8 text-xs w-[60px]">Diff</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {DIFF_FEE_LABELS.map((fee) => {
                                                            const cur = m.currentFees?.[fee.key];
                                                            const nw = m.newFees[fee.key];
                                                            const changed = cur !== nw;
                                                            if (cur == null && nw == null) return null;

                                                            return (
                                                                <TableRow
                                                                    key={fee.key}
                                                                    className={changed ? 'bg-amber-50/50' : ''}
                                                                >
                                                                    <TableCell className="py-1 text-xs font-medium">
                                                                        {fee.label}
                                                                    </TableCell>
                                                                    <TableCell className="py-1 font-mono text-xs">
                                                                        {formatDiffValue(cur, fee.format)}
                                                                    </TableCell>
                                                                    <TableCell className="py-1 font-mono text-xs">
                                                                        {formatDiffValue(nw, fee.format)}
                                                                    </TableCell>
                                                                    <TableCell className="py-1 text-xs">
                                                                        {changed ? (
                                                                            <Badge variant="secondary" className="h-5 bg-amber-100 text-amber-800 text-[10px]">
                                                                                changed
                                                                            </Badge>
                                                                        ) : (
                                                                            <span className="text-green-600">—</span>
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}
